/**
 * Costa Rican taxpayer identification. Hacienda's public API takes the bare
 * digits; the type codes match electronic-receipt v4.4 (01..04).
 */
export type IdType = "FISICA" | "JURIDICA" | "DIMEX" | "NITE";
export type TaxpayerType = "PERSONA_FISICA" | "PERSONA_JURIDICA";

export const ID_TYPE_CODE: Record<IdType, "01" | "02" | "03" | "04"> = {
  FISICA: "01",
  JURIDICA: "02",
  DIMEX: "03",
  NITE: "04",
};

export function idTypeFromCode(code: string): IdType | null {
  const found = (Object.entries(ID_TYPE_CODE) as [IdType, string][]).find(([, c]) => c === code);
  return found ? found[0] : null;
}

export function normalizeId(raw: string): string {
  return raw.replace(/[\s.-]/g, "");
}

export type IdValidation = { ok: true; id: string; type: IdType } | { ok: false; reason: string };

/**
 * Structural validation only (length, leading digit). Whether the ID exists is
 * answered by Hacienda's public API, never guessed here.
 */
export function validateId(raw: string, type: IdType): IdValidation {
  const id = normalizeId(raw);
  if (!/^\d+$/.test(id)) return { ok: false, reason: "Solo se permiten dígitos" };
  switch (type) {
    case "FISICA":
      if (id.length !== 9 || id.startsWith("0")) return { ok: false, reason: "Cédula física: 9 dígitos, sin cero inicial" };
      break;
    case "JURIDICA":
      // 3-xxx private entities; 2-xxx / 4-xxx public institutions (e.g. ICE 4-000-042139).
      if (id.length !== 10 || !/^[234]/.test(id)) return { ok: false, reason: "Cédula jurídica: 10 dígitos que inician con 2, 3 o 4" };
      break;
    case "DIMEX":
      if (id.length !== 11 && id.length !== 12) return { ok: false, reason: "DIMEX: 11 o 12 dígitos" };
      break;
    case "NITE":
      if (id.length !== 10) return { ok: false, reason: "NITE: 10 dígitos" };
      break;
  }
  return { ok: true, id, type };
}

export function taxpayerTypeOf(type: IdType): TaxpayerType {
  return type === "JURIDICA" ? "PERSONA_JURIDICA" : "PERSONA_FISICA";
}

/** Mask all but the last 4 digits for logs and shared documents. */
export function maskId(id: string): string {
  const n = normalizeId(id);
  return n.length <= 4 ? n : "•".repeat(n.length - 4) + n.slice(-4);
}
